import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow } from '~/components/common';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { ICRMStatus } from '~/utils/interface/crm-status';
import { handleCreateCRMStatus, handleEditCRMStatus } from '~/thunks/crm-status/crmStatusThunk';
import { crmStatusActions } from '~/thunks/crm-status/crmStatusSlice';

type EditCRMStatusDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: ICRMStatus;
  children?: React.ReactNode;
};

const initialValues: ICRMStatus = {
  name: '',
};

const EditCRMStatusDialog = (props: EditCRMStatusDialogProps) => {
  const { mode = DIALOG_MODE_CREATE, data } = props;

  const { t } = useTranslation(['admin/crm-status']);
  const dispatch = useAppDispatch();

  const { crmStatusId } = useAppSelector((state) => state.crmStatus);
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateCRMStatus(values)).unwrap();
          dispatch(crmStatusActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditCRMStatus({ id: crmStatusId, params: values })).unwrap();
          dispatch(crmStatusActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.name')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('properties.name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditCRMStatusDialog);
